import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Eye, EyeOff, Users, DollarSign, Clock,
  FileText, ChevronDown, ChevronUp, ExternalLink, HelpCircle,
  Shield, Gavel, TrendingUp, RefreshCw, Plus, CheckCircle2
} from 'lucide-react';
import { toast } from 'react-toastify';

const WARDS = ['ward-1','ward-2','ward-3','ward-4','ward-5','ward-6','ward-7','ward-8','ward-9','ward-10'];

const FLAG_CONFIG = {
  in_camera: { icon: EyeOff, label: 'In-Camera Abuse', color: 'text-red-500', bg: 'bg-red-500/10' },
  procedural_shortcut: { icon: Gavel, label: 'Procedural Shortcut', color: 'text-orange-500', bg: 'bg-orange-500/10' },
  referral_trap: { icon: Clock, label: 'Referral Trap', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  consultant_creep: { icon: DollarSign, label: 'Consultant Creep', color: 'text-purple-500', bg: 'bg-purple-500/10' },
  voting_block: { icon: Users, label: 'Voting Block', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  logic_conflict: { icon: TrendingUp, label: 'Logic Conflict', color: 'text-pink-500', bg: 'bg-pink-500/10' },
  expenditure: { icon: DollarSign, label: 'Major Expenditure', color: 'text-green-500', bg: 'bg-green-500/10' },
  other: { icon: AlertTriangle, label: 'Other', color: 'text-gray-500', bg: 'bg-gray-500/10' },
};

const SEVERITY_CONFIG = {
  critical: { pill: 'bg-red-600 text-black', label: 'CRITICAL' },
  high: { pill: 'bg-orange-500 text-black', label: 'HIGH' },
  medium: { pill: 'bg-yellow-500 text-black', label: 'MEDIUM' },
  low: { pill: 'bg-gray-500 text-white', label: 'LOW' },
};

// ── Single flag card ──────────────────────────────────────────────────────────
const FlagCard = ({ flag, expanded, onToggle, onAddToPulse, wardForCouncillor }) => {
  const cfg = FLAG_CONFIG[flag.flag_type] || FLAG_CONFIG.other;
  const sev = SEVERITY_CONFIG[flag.severity] || SEVERITY_CONFIG.medium;
  const Icon = cfg.icon;

  const hasUniqueEvidence = flag.evidence && flag.evidence.trim() !== flag.title?.trim();

  // Auto-detect ward: prefer AI's suggested_ward, fallback to councillors lookup
  const rawWard = flag.suggested_ward ||
    (flag.councillors_involved?.length > 0 ? wardForCouncillor(flag.councillors_involved[0]) : null);
  // Only show valid wards in the button label
  const detectedWard = rawWard && WARDS.includes(rawWard) ? rawWard : null;

  // Construct source link from numeric meeting_id
  const sourceUrl = flag.meeting_id
    ? `https://reginask.iqm2.com/Citizens/Detail_Meeting.aspx?ID=${flag.meeting_id}`
    : null;

  return (
    <div className={`${cfg.bg} border border-[#1a1a1a] rounded-lg overflow-hidden`} data-testid={`flag-${flag.id}`}>
      <div className="p-4 cursor-pointer flex items-start gap-3" onClick={onToggle}>
        <div className={`p-2 rounded-lg ${cfg.bg} flex-shrink-0`}><Icon className={`w-4 h-4 ${cfg.color}`} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${sev.pill}`}>{sev.label}</span>
            <span className="text-xs text-gray-500 font-mono">{flag.committee || ''}</span>
            {detectedWard && (
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-[#C5A52A]/15 text-[#C5A52A] border border-[#C5A52A]/30">
                {detectedWard}
              </span>
            )}
          </div>
          <p className="text-white font-semibold text-sm leading-snug">{flag.title}</p>
          <p className="text-gray-500 text-xs mt-0.5">{flag.meeting_date || ''}</p>
        </div>
        <button className="text-gray-500 p-1 flex-shrink-0 flex items-center gap-1">
          <span className="text-[10px] text-gray-600 font-mono">{expanded ? 'LESS' : 'MORE'}</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-5 border-t border-[#1a1a1a] pt-4 space-y-4">
          {flag.councillors_involved?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-mono">Councillors Involved</p>
              <div className="flex flex-wrap gap-1.5">
                {flag.councillors_involved.map((n, i) => {
                  const w = wardForCouncillor(n);
                  return (
                    <span key={i} className={`text-sm font-medium px-3 py-1 rounded-full ${cfg.bg} ${cfg.color} border border-current/20`}>
                      {n}{w ? ` · ${w}` : ''}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {hasUniqueEvidence && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-mono">Evidence from Minutes</p>
              <blockquote className="text-sm text-gray-300 italic border-l-2 border-[#C5A52A] pl-3 leading-relaxed">
                "{flag.evidence}"
              </blockquote>
            </div>
          )}
          {flag.citizen_question ? (
            <div className="bg-[#C5A52A]/15 border border-[#C5A52A]/40 rounded-lg p-4">
              <p className="text-xs font-bold text-[#C5A52A] uppercase tracking-widest mb-2 flex items-center gap-1.5 font-mono">
                <HelpCircle className="w-3.5 h-3.5" /> Ask Your Councillor
              </p>
              <p className="text-base text-white font-medium leading-relaxed mb-3">{flag.citizen_question}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={(e) => { e.stopPropagation(); onAddToPulse(flag, detectedWard); }}
                  className="flex items-center gap-1.5 text-xs font-mono font-bold text-[#C5A52A] hover:text-yellow-300 border border-[#C5A52A]/40 hover:border-[#C5A52A] px-3 py-1.5 rounded transition-colors"
                  data-testid={`flag-add-to-pulse-${flag.id}`}
                >
                  <Plus className="w-3.5 h-3.5" /> ADD TO WARD PULSE{detectedWard ? ` (${detectedWard})` : ''}
                </button>
                {sourceUrl && (
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1.5 text-xs font-mono text-gray-500 hover:text-gray-300 border border-zinc-700 hover:border-zinc-500 px-3 py-1.5 rounded transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" /> SOURCE DOC
                  </a>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600 italic">No citizen question generated for this flag.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Add to Ward Pulse modal ───────────────────────────────────────────────────
const AddToPulseModal = ({ question, onClose, onAdd }) => {
  const validWard = question.suggestedWard && WARDS.includes(question.suggestedWard)
    ? question.suggestedWard
    : 'ward-1';
  const [ward, setWard] = useState(validWard);
  const [threshold, setThreshold] = useState(25);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onAdd({ ward, threshold });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-[#2a2a2a] rounded-lg w-full max-w-md p-6">
        <h3 className="text-white font-bold text-lg mb-1">Add to Ward Pulse</h3>
        <p className="text-gray-500 text-xs mb-4">This question will become a draft Ward Pulse issue for residents to amplify.</p>
        <div className="bg-[#C5A52A]/10 border border-[#C5A52A]/30 rounded p-3 mb-4">
          <p className="text-sm text-white">{question.question}</p>
          <p className="text-xs text-gray-500 mt-1">{question.committee} · {question.meeting_date}</p>
        </div>
        <div className="space-y-3 mb-6">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">Target Ward</label>
            <select
              value={ward}
              onChange={e => setWard(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white"
              data-testid="ward-pulse-ward-select"
            >
              {WARDS.map(w => (
                <option key={w} value={w}>{w.replace('-', ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">Threshold (asks needed)</label>
            <input
              type="number"
              value={threshold}
              onChange={e => setThreshold(parseInt(e.target.value) || 25)}
              min={5}
              max={200}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white"
              data-testid="ward-pulse-threshold"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 rounded bg-gray-800 text-gray-300 text-sm hover:bg-gray-700">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="confirm-add-to-pulse"
            className="flex-1 py-2 rounded bg-[#C5A52A] text-black font-bold text-sm hover:bg-yellow-400 disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add as Draft'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main AuditorTab ───────────────────────────────────────────────────────────
const AuditorTab = ({ api }) => {
  const [flags, setFlags] = useState([]);
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFlag, setExpandedFlag] = useState(null);
  const [filter, setFilter] = useState({ severity: '', type: '' });
  const [pulseModal, setPulseModal] = useState(null);
  const [councillors, setCouncillors] = useState([]);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptIsCustom, setPromptIsCustom] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [flagsRes, statsRes, questionsRes, meetingsRes] = await Promise.all([
        api.get('/council-audit/flags?limit=100'),
        api.get('/council-audit/stats'),
        api.get('/council-audit/citizen-questions?limit=30'),
        api.get('/council-audit/meetings?limit=20'),
      ]);
      setFlags(flagsRes.data.flags || []);
      setStats(statsRes.data);
      setQuestions(questionsRes.data.questions || []);
      setMeetings(meetingsRes.data.meetings || []);

      try {
        const cRes = await api.get('/councillors');
        setCouncillors(cRes.data || []);
      } catch {}

      try {
        const pRes = await api.get('/admin/audit-prompt');
        setPromptText(pRes.data.prompt || '');
        setPromptIsCustom(pRes.data.is_custom || false);
      } catch {}
    } catch (err) {
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Map councillor last name → ward (e.g. "Mancinelli" → "ward-8")
  const wardForCouncillor = useCallback((lastName) => {
    if (!lastName || !councillors.length) return null;
    const lc = lastName.toLowerCase().trim();
    const match = councillors.find(c =>
      (c.match_name || c.name || '').toLowerCase().includes(lc) ||
      (c.name || '').toLowerCase().split(' ').pop() === lc
    );
    if (!match?.ward) return null;
    // Normalize "Ward 8" → "ward-8"
    return match.ward.toLowerCase().replace(' ', '-');
  }, [councillors]);

  const handleAddToPulse = async ({ ward, threshold }) => {
    try {
      await api.post('/admin/ward-pulse/from-audit', {
        question: pulseModal.question,
        meeting_id: pulseModal.meeting_id,
        committee: pulseModal.committee,
        meeting_date: pulseModal.meeting_date,
        ward,
        threshold,
      });
      toast.success('Added to Ward Pulse as a draft issue.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add to Ward Pulse');
      throw err;
    }
  };

  const filteredFlags = flags.filter(f =>
    (!filter.severity || f.severity === filter.severity) &&
    (!filter.type || f.flag_type === filter.type)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-[#C5A52A]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="auditor-tab">
      {pulseModal && (
        <AddToPulseModal
          question={pulseModal}
          onClose={() => setPulseModal(null)}
          onAdd={handleAddToPulse}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-[#C5A52A]" />
          <div>
            <h2 className="text-2xl font-bold text-white">Council Auditor</h2>
            <p className="text-xs text-gray-500 font-mono">Admin-only · AI forensic analysis of City of Regina council decisions</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded text-sm hover:bg-gray-700"
          data-testid="refresh-audit"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Trigger Panel removed — AI automation disabled */}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: FileText, label: 'Meetings Audited', value: stats.total_meetings_audited || 0, color: 'bg-blue-600' },
            { icon: AlertTriangle, label: 'Total Flags', value: stats.total_flags || 0, color: 'bg-yellow-600' },
            { icon: EyeOff, label: 'In-Camera Issues', value: stats.flags_by_type?.in_camera || 0, color: 'bg-red-600' },
            { icon: DollarSign, label: 'Expenditure Flags', value: stats.flags_by_type?.expenditure || 0, color: 'bg-green-600' },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4 flex items-center gap-3">
              <div className={`p-2 rounded ${color}`}><Icon className="w-4 h-4 text-white" /></div>
              <div>
                <p className="text-xl font-bold text-white">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Main content: flags + citizen questions side by side */}
      <div className="grid lg:grid-cols-3 gap-6">

        {/* Flags Column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-white font-bold text-base flex-1">Audit Flags</h3>
            <select
              value={filter.severity}
              onChange={e => setFilter(f => ({ ...f, severity: e.target.value }))}
              className="bg-[#0a0a0a] border border-[#1a1a1a] rounded px-2 py-1.5 text-xs text-white"
              data-testid="severity-filter"
            >
              <option value="">All Severities</option>
              {['critical','high','medium','low'].map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <select
              value={filter.type}
              onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
              className="bg-[#0a0a0a] border border-[#1a1a1a] rounded px-2 py-1.5 text-xs text-white"
              data-testid="type-filter"
            >
              <option value="">All Types</option>
              {Object.entries(FLAG_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <span className="text-gray-600 text-xs font-mono">{filteredFlags.length} flags</span>
          </div>

          <div className="space-y-2" data-testid="flags-list">
            {filteredFlags.length === 0 ? (
              <div className="text-center py-12 text-gray-600 text-sm">
                {flags.length === 0 ? 'No audit data yet. Run an audit above.' : 'No flags match your filters.'}
              </div>
            ) : filteredFlags.map(flag => (
              <FlagCard
                key={flag.id}
                flag={flag}
                expanded={expandedFlag === flag.id}
                onToggle={() => setExpandedFlag(expandedFlag === flag.id ? null : flag.id)}
                wardForCouncillor={wardForCouncillor}
                onAddToPulse={(flag, detectedWard) => setPulseModal({
                  question: flag.citizen_question,
                  meeting_id: flag.meeting_id,
                  committee: flag.committee,
                  meeting_date: flag.meeting_date,
                  suggestedWard: detectedWard,
                })}
              />
            ))}
          </div>
        </div>

        {/* Sidebar: Citizen Questions */}
        <div className="space-y-4">
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4" data-testid="citizen-questions">
            <h3 className="text-[#C5A52A] font-bold text-sm uppercase tracking-wide mb-1 flex items-center gap-2">
              <HelpCircle className="w-4 h-4" /> Citizen Ammunition
            </h3>
            <p className="text-sm text-gray-500 mb-4">AI-generated questions from audited meetings. Add any to Ward Pulse so residents can amplify them.</p>
            {questions.length === 0 ? (
              <p className="text-gray-600 text-sm">No questions yet. Run an audit first.</p>
            ) : (
              <div className="space-y-3" data-testid="questions-list">
                {questions.map((q, idx) => (
                  <div key={idx} className="border border-[#2a2a2a] rounded-lg p-4 space-y-3 hover:border-[#C5A52A]/30 transition-colors">
                    <p className="text-base text-white leading-relaxed font-medium">{q.question}</p>
                    <p className="text-xs text-gray-500 font-mono">{q.committee} · {q.meeting_date}</p>
                    <button
                      onClick={() => setPulseModal(q)}
                      data-testid={`add-to-pulse-${idx}`}
                      className="flex items-center gap-1.5 text-sm text-[#C5A52A] hover:text-yellow-300 font-mono transition-colors font-bold"
                    >
                      <Plus className="w-3.5 h-3.5" /> ADD TO WARD PULSE
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Audited Meetings List */}
          {meetings.length > 0 && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
              <h3 className="text-white font-bold text-xs uppercase tracking-wide mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-500" /> Audited Meetings
              </h3>
              <div className="space-y-2">
                {meetings.slice(0, 8).map(m => (
                  <div key={m.id || m.meeting_id} className="text-xs text-gray-400 flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{m.committee} · {m.meeting_date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Liability Notice */}
          <div className="bg-red-900/10 border border-red-900/30 rounded-lg p-4">
            <h3 className="text-red-400 font-bold text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Admin Eyes Only
            </h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              This data is AI-generated and may contain errors. It must never be published publicly without human review. Raw audit flags are not shown to the public to avoid defamation liability.
            </p>
          </div>

          {/* AI Prompt Editor */}
          <div className="border border-zinc-800 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowPromptEditor(v => !v)}
              className="w-full flex items-center justify-between px-5 py-3 bg-[#0a0a0a] hover:bg-[#111] transition-colors"
              data-testid="toggle-prompt-editor"
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-400 uppercase tracking-wider">Edit AI Audit Prompt</span>
                {promptIsCustom && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#C5A52A]/20 text-[#C5A52A] border border-[#C5A52A]/30">CUSTOM</span>
                )}
              </div>
              {showPromptEditor ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </button>

            {showPromptEditor && (
              <div className="p-4 bg-[#0d0d0d] space-y-3">
                <p className="text-xs text-gray-600 font-mono">
                  This is the instruction set sent to Gemini before each meeting is analyzed. Changes take effect on the next audit run.
                  {promptIsCustom && <span className="text-yellow-500 ml-2">Custom version active.</span>}
                </p>
                <textarea
                  value={promptText}
                  onChange={e => setPromptText(e.target.value)}
                  rows={20}
                  className="w-full bg-[#111] border border-zinc-700 rounded p-3 text-xs text-gray-200 font-mono leading-relaxed focus:outline-none focus:border-[#C5A52A] resize-y"
                  data-testid="prompt-textarea"
                />
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      setSavingPrompt(true);
                      try {
                        await api.put('/admin/audit-prompt', { prompt: promptText });
                        setPromptIsCustom(true);
                        toast.success('Prompt saved — will be used on next audit run');
                      } catch { toast.error('Failed to save prompt'); }
                      finally { setSavingPrompt(false); }
                    }}
                    disabled={savingPrompt}
                    className="px-4 py-2 bg-[#C5A52A] hover:bg-yellow-400 text-black font-mono text-xs font-bold rounded disabled:opacity-50 transition-colors"
                    data-testid="save-prompt-btn"
                  >
                    {savingPrompt ? 'SAVING…' : 'SAVE PROMPT'}
                  </button>
                  {promptIsCustom && (
                    <button
                      onClick={async () => {
                        try {
                          await api.delete('/admin/audit-prompt');
                          const pRes = await api.get('/admin/audit-prompt');
                          setPromptText(pRes.data.prompt || '');
                          setPromptIsCustom(false);
                          toast.success('Reverted to default prompt');
                        } catch { toast.error('Failed to reset'); }
                      }}
                      className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-gray-300 font-mono text-xs rounded transition-colors"
                      data-testid="reset-prompt-btn"
                    >
                      RESET TO DEFAULT
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default AuditorTab;
